import { Component, Input, OnInit } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { LanguageSheetService, LanguageSheetMode } from '../../services/language-sheet.service';
import { AnalyticsService } from '../../services/analytics.service';

type RowVariant = 'default' | 'suggested' | 'current';

interface SheetRow {
  code: string;
  nativeName: string;
  flag: string;
  showFlag: boolean;
  variant: RowVariant;
}

/**
 * Language-selection bottom sheet. Snapshots the venue languages + active language once at open
 * (the sheet is recreated per open via *ngIf in AppComponent, so a snapshot is always fresh and
 * avoids rebuilding rows mid-view when a background refreshLanguages re-emits venueLanguages$).
 *
 * The title and the pinned row's "Suggested"/"Current" tag render in the PINNED language's own
 * locale (mirrors Android), resolved via LanguageService.getLocaleTranslations without switching
 * the active UI language.
 */
@Component({
    selector: 'app-language-sheet',
    templateUrl: './language-sheet.component.html',
    styleUrls: ['./language-sheet.component.scss'],
    standalone: false
})
export class LanguageSheetComponent implements OnInit {
  @Input() mode: LanguageSheetMode = 'first';

  rows: SheetRow[] = [];
  titleText = '';
  tagText = '';

  constructor(
    private languageService: LanguageService,
    private sheet: LanguageSheetService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    const venue = this.languageService.getVenueLanguages();

    // First visit pins the device-suggested language; Switch pins the one already applied.
    const pinnedCode = this.mode === 'switch'
      ? this.languageService.getCurrentLang()
      : this.languageService.getSuggestedLanguageCode();
    const pinned = pinnedCode ? venue.find(v => v.code === pinnedCode) ?? null : null;

    // Everything below the pin keeps server order, except English floats to the top of the
    // remainder (stable sort) so it is conveniently first when nothing is pinned.
    const rest = venue.filter(v => v !== pinned);
    rest.sort((a, b) => (b.code === 'en' ? 1 : 0) - (a.code === 'en' ? 1 : 0));

    const ordered = pinned ? [pinned, ...rest] : rest;
    const pinnedVariant: RowVariant = this.mode === 'switch' ? 'current' : 'suggested';

    this.rows = ordered.map(v => ({
      code: v.code,
      nativeName: v.nativeName || v.name || v.code.toUpperCase(),
      flag: v.flag_value || '',
      showFlag: !!(v.flag_value && v.flag_value.trim()),
      variant: v === pinned ? pinnedVariant : 'default'
    }));

    const titleLocale = pinnedCode ?? ordered[0]?.code ?? 'en';
    this.languageService.getLocaleTranslations(titleLocale).then(bundle => {
      const strings = (bundle && bundle.menu && bundle.menu.language) || {};
      this.titleText = this.mode === 'switch'
        ? (strings.switchTitle || '')
        : (strings.sheetTitle || '');
      this.tagText = this.mode === 'switch'
        ? (strings.current || '')
        : (strings.suggested || '');
    });
  }

  // One tap = remember per-device + apply + dismiss. No confirm button.
  select(code: string): void {
    // Read BEFORE the switch is applied — the language being left is the direction of the change,
    // and it is gone a line later. Both codes are passed explicitly so the event does not depend on
    // firing after the change lands.
    const previous = this.languageService.getCurrentLang();
    this.languageService.chooseLanguage(code);
    this.analytics.log('language_changed', { app_language: code, previous_language: previous });
    this.sheet.close();
  }

  // X / scrim / Back: dismiss without changing (nothing persisted → sheet re-offers next visit).
  dismiss(): void {
    this.sheet.close();
  }
}
